import React, { memo, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Typography,
  Card,
  CardContent,
  Stack,
  CopyToClipboard,
  Icon,
  IconButton,
  Tooltip
} from 'investira.react.components';
import { dates, formats, validators } from 'investira.sdk';
import { HighchartsReact } from 'investira.react.charts';
import PropTypes from 'prop-types';
import services from '../../../services';
import { useEmpresasContext } from '../../../contexts/EmpresasContext';
import CardState from './CardState';
import CardFooter from './CardFooter';

/**
 * Renderiza o componente CapitalSocialCard.
 *
 * @param {Object} props - Propriedades do componente.
 * @param {number} props.capitalSocial - Propriedade `capitalSocial` do componente.
 * @param {Object} props.actions - Propriedade `actions` do componente.
 * @param {Function} props.formatNumber - Propriedade `formatNumber` do componente.
 * @param {Function} props.onOpenHistory - Propriedade `onOpenHistory` do componente.
 * @param {boolean} props.showChart - Propriedade `showChart` do componente.
 * @param {boolean} props.showHistoryButton - Propriedade `showHistoryButton` do componente.
 * @param {Object} props.sx - Propriedade `sx` do componente.
 *
 * @description
 * Este componente realiza as seguintes operacoes:
 * 1. Recebe as propriedades necessarias para compor a interface
 * 2. Organiza os estados e callbacks usados durante a renderizacao
 * 3. Renderiza a experiencia visual do organismo
 *
 * @returns {JSX.Element|null} Elemento renderizado pelo componente.
 */

const CapitalSocialCard = memo(props => {
  const params = useParams();
  const {
    pessoaId,
    capitalSocial,
    actions: pActions,
    onOpenHistory,
    showChart = true,
    showHistoryButton = true,
    sx,
    ...otherProps
  } = props;

  const xContext = useEmpresasContext();
  const { state = {}, actions: xContextActions = {} } = xContext || {};

  const xPessoaId = pessoaId || params?.pessoa_id;

  const [capital, setCapital] = useState({});
  const [historicoLocal, setHistoricoLocal] = useState([]);
  const [isLoading, setLoading] = useState(true);

  const xHistorico = validators.isEmpty(state?.historico?.cs) ? historicoLocal : state.historico.cs;

  const xChartData = useMemo(() => {
    const xData = [];

    xHistorico.forEach(xPoint => {
      const xDate = dates.toDate(xPoint.data).getTime();
      const xPapeis = xPoint?.acao ? Object.keys(xPoint.acao) : [];

      if (validators.isEmpty(xData[0])) {
        xData[0] = [];
      }

      xData[0].push([xDate, xPoint.cs]);

      if (xPapeis.includes('on')) {
        if (validators.isEmpty(xData[1])) {
          xData[1] = [];
        }

        xData[1].push([xDate, xPoint.acao.on]);
      }

      if (xPapeis.includes('pn')) {
        if (validators.isEmpty(xData[2])) {
          xData[2] = [];
        }

        xData[2].push([xDate, xPoint.acao.pn]);
      }
    });

    return xData;
  }, [xHistorico]);

  const xChartOptions = useMemo(
    () => ({
      title: false,
      chart: {
        height: 120,
        margin: [0, 0, 0, 0],
        spacing: [0, 0, 0, 0]
      },
      legend: { enabled: false },
      yAxis: [
        {
          type: 'linear',
          title: { text: 'Capital Social (R$)' },
          showEmpty: false,
          visible: false
        },
        { type: 'linear', title: { text: 'ON' }, visible: false, showEmpty: false },
        { type: 'linear', title: { text: 'PN' }, visible: false, showEmpty: false }
      ],
      xAxis: { type: 'datetime', crosshair: true, visible: false },
      responsive: {
        condition: {
          maxWidth: 500
        }
      },
      series: [
        {
          name: 'Capital Social',
          type: 'line',
          data: xChartData[0] || [],
          connectNulls: true,
          zIndex: 10,
          showInLegend: Boolean(xChartData[0]),
          marker: { enabled: false }
        },
        {
          name: 'ON',
          type: 'column',
          data: xChartData[1] || [],
          connectNulls: true,
          yAxis: 1,
          showInLegend: Boolean(xChartData[1]),
          pointWidth: 10,
          opacity: 1
        },
        {
          name: 'PN',
          type: 'column',
          data: xChartData[2] || [],
          connectNulls: true,
          yAxis: 2,
          showInLegend: Boolean(xChartData[2]),
          pointWidth: 10,
          opacity: 1
        }
      ],
      tooltip: {
        formatter: function () {
          const xDate = formats.formatDateCustom(this.point.x, 'DD/MMM/YYYY');
          let xHTML = `<span style="font-size: 1em"><b>${xDate}</b></span><br/>`;
          let xTotalPapeis = 0;

          (this.points || []).forEach(xPoint => {
            if (['ON', 'PN'].includes(xPoint.series.name)) {
              xTotalPapeis += xPoint.point.y;
            }

            const xValue = formats.friendlyNumber(xPoint.point.y, 2, false);
            const xFullValue = formats.formatNumber(xPoint.point.y, 0, true, false);

            xHTML += `<span style="color:${xPoint.color}">●</span>${xPoint.series.name}: <b>${xValue}</b> (${xFullValue})<br/>`;
          });

          xHTML += `Total de Papéis: <b>${formats.friendlyNumber(
            xTotalPapeis,
            2,
            false
          )}</b> (${formats.formatNumber(xTotalPapeis, 0, true, false)})<br/>`;

          return xHTML;
        },
        shared: true
      }
    }),
    [xChartData]
  );

  useEffect(() => {
    if (!xPessoaId) {
      setLoading(false);
      return;
    }

    // Se o componente está dentro do EmpresasProvider, os dados virão do contexto.
    if (xContext) {
      // Se o histórico do contexto ainda não carregou, exibe o loading e aguarda.
      if (validators.isEmpty(state?.historico?.cs)) {
        setLoading(true);
      } else {
        // Se o histórico carregou, pega o valor mais recente.
        // O histórico do contexto (state.historico.cs) já vem ordenado por data ASC.
        const xMostRecentCapital = state.historico.cs[state.historico.cs.length - 1];
        setCapital(xMostRecentCapital || {});
        setLoading(false);
      }
      return; // Impede a execução da busca de dados do modo standalone.
    }

    // Lógica para modo standalone (quando não há contexto)
    setLoading(true);
    services.pessoas.cs(
      {
        data: dates.toSqlDate(dates.toDate()),
        pessoa_id: xPessoaId
      },
      rRes => {
        setCapital(rRes?.data?.[0] || {});
      },
      null,
      () => {
        setLoading(false);
      }
    );

    return () => services.pessoas?.cancel?.cs?.cancel && services.pessoas.cancel.cs.cancel();
  }, [xPessoaId, xContext, state?.historico?.cs]);

  useEffect(() => {
    if (!xPessoaId || !validators.isEmpty(state?.historico?.cs)) {
      return;
    }

    const xToday = new Date();

    services.pessoas.cs(
      {
        data: null,
        data_inicio: dates.addYears(dates.toSqlDate(xToday), -10),
        data_fim: dates.toSqlDate(xToday),
        pessoa_id: xPessoaId,
        sort: 'data ASC'
      },
      rRes => {
        setHistoricoLocal(rRes?.data || []);
      },
      null,
      () => {}
    );
  }, [xPessoaId, state?.historico?.cs]);

  const xValue = !validators.isNull(capitalSocial) ? capitalSocial : capital?.cs;
  const xShowCard = !validators.isNull(xValue) || !validators.isEmpty(capital);

  const formatNumber = (pValue, pCurrency = false, pDecimals = 0) => {
    if (validators.isNull(pValue)) {
      return '-';
    }

    if (pActions?.formatNumber) {
      return pActions.formatNumber(pValue);
    }

    if (xContextActions?.formatNumber) {
      return xContextActions.formatNumber(pValue, state?.showPrecise, pCurrency, pDecimals, null);
    }

    return pCurrency
      ? formats.friendlyNumber(pValue, 2, false)
      : formats.formatNumber(pValue, 0, false, false);
  };

  function somaPapeis(pOn, pPn) {
    const xOn = pOn || 0;
    const xPn = pPn || 0;

    return xOn + xPn;
  }

  const handleOpenClick = () => {
    if (onOpenHistory) {
      return onOpenHistory('cs');
    }

    xContextActions?.handleChartVariantChange && xContextActions.handleChartVariantChange('cs');
  };

  if (!isLoading && !xShowCard) {
    return null;
  }

  return (
    <Stack
      sx={{
        flex: 1,
        minWidth: 0,
        ...sx
      }}
      aria-label="Capital social"
      {...otherProps}>
      <Card sx={{ minHeight: '100%' }}>
        <CardContent sx={{ minHeight: '100%', display: 'flex', flexDirection: 'column' }}>
          <Stack direction="row" spacing={2}>
            <Stack flexGrow={1}>
              <Typography variant="caption" color="textPrimary" gutterBottom component="h4">
                Capital social
              </Typography>
            </Stack>
            {xShowCard && showHistoryButton && (
              <Stack>
                <Tooltip title="Histórico Capital Social">
                  <div>
                    <IconButton color="primary" onClick={handleOpenClick}>
                      <Icon iconName="chart_bar" size={16} />
                    </IconButton>
                  </div>
                </Tooltip>
              </Stack>
            )}
          </Stack>

          <CardState
            loading={isLoading}
            data={!validators.isNull(xValue) ? { cs: xValue } : capital}>
            <Stack spacing={1} justifyContent="space-between" sx={{ minHeight: '100%' }}>
              <Stack>
                <Typography variant="h4" color="textPrimary" sx={{ fontWeight: 600 }}>
                  {formatNumber(xValue, true, 2)}
                </Typography>

                {!validators.isNull(capital?.data) && (
                  <Typography variant="caption" color="textPrimary" gutterBottom>
                    Data de referência {formats.formatDateCustom(capital?.data, 'DD/MMM/YYYY')}
                  </Typography>
                )}
              </Stack>

              {showChart && <HighchartsReact options={xChartOptions} />}

              <Stack direction="row" justifyContent="flex-start" spacing={4}>
                <Stack>
                  <Typography variant="caption" color="textSecondary">
                    Total de Papéis
                  </Typography>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography variant="body1" color="textPrimary" sx={{ fontWeight: 600 }}>
                      {formatNumber(somaPapeis(capital?.acao?.on, capital?.acao?.pn), false, 0)}
                    </Typography>
                    <Stack>
                      <CopyToClipboard value={somaPapeis(capital?.acao?.on, capital?.acao?.pn)} />
                    </Stack>
                  </Stack>
                </Stack>

                {capital?.acao?.on && (
                  <Stack>
                    <Typography variant="caption" color="textSecondary">
                      ON
                    </Typography>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Typography variant="body1" color="textPrimary" sx={{ fontWeight: 600 }}>
                        {formatNumber(capital?.acao?.on, false, 0)}
                      </Typography>
                      <Stack>
                        <CopyToClipboard value={capital?.acao?.on} />
                      </Stack>
                    </Stack>
                  </Stack>
                )}

                {capital?.acao?.pn && (
                  <Stack>
                    <Typography variant="caption" color="textSecondary">
                      PN
                    </Typography>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Typography variant="body1" color="textPrimary" sx={{ fontWeight: 600 }}>
                        {formatNumber(capital?.acao?.pn, false, 0)}
                      </Typography>
                      <Stack>
                        <CopyToClipboard value={capital?.acao?.pn} />
                      </Stack>
                    </Stack>
                  </Stack>
                )}
              </Stack>

              <CardFooter
                consolidacao={capital?.consolidacao_tipo}
                atualizado={capital?.data}
                origem={capital?.origem}
                pc={capital?.pc}
              />
            </Stack>
          </CardState>
        </CardContent>
      </Card>
    </Stack>
  );
});

CapitalSocialCard.displayName = 'CapitalSocialCard';

CapitalSocialCard.propTypes = {
  pessoaId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  capitalSocial: PropTypes.number,
  actions: PropTypes.shape({
    formatNumber: PropTypes.func
  }),
  onOpenHistory: PropTypes.func,
  showChart: PropTypes.bool,
  showHistoryButton: PropTypes.bool,
  sx: PropTypes.object
};

export default CapitalSocialCard;
